import { ts } from '@shared/lib/diagTs';
import { create } from 'zustand';

/**
 * Global date-range scope — the app-wide "review window", a sibling of the global account
 * (`accountStore`). Every page that date-filters reads it via `useAnalyticsFilterParams`, so changing
 * the range in the top-bar Date picker re-keys every date-bound query in TanStack Query automatically
 * (exactly like flipping the account).
 *
 * Persisted to localStorage with a synchronous hydrate at module-load (same approach as `accountStore`,
 * not Zustand's `persist` middleware) so the first render already has the right window — no flash.
 * Dates are inclusive `YYYY-MM-DD` calendar days; `useAnalyticsFilterParams` turns them into
 * `[from's start, to's last millisecond]` bounds in the PROFILE zone (matching the old custom-range
 * behaviour; the bounds were UTC days, `T00:00:00Z` / `T23:59:59.999Z`, until 2026-09-28).
 *
 * **Last-import anchoring (`isDefault`)** — on a *fresh* visitor (nothing persisted yet) the default
 * window is the device's last 7 days, but the device "today" may be ahead of the user's most recent
 * imported trade (e.g. they import a CSV that ends last Friday). To keep new imports in view, the
 * store carries an `isDefault` flag: while it's still `true` (the user hasn't picked a range and there
 * was no persisted selection) `seedFromLastImport(ymd)` re-anchors the window's END to the account's
 * last-import day (preserving the 7-day length). The moment the user picks a range (`setRange`) the
 * flag flips to `false` and we never re-anchor again. Anything restored from localStorage is treated
 * as an explicit, sticky selection (`isDefault: false`) and is likewise never re-anchored.
 *
 * **Rolling the END forward (2026-09-15)** — a picked range used to freeze its `to` day forever, so a
 * trader who touched the picker once saw their window quietly end in the past every following day and
 * had to fix it by hand. On every app load `extendToToday` now moves a persisted `to` that is in the
 * PAST up to the device's today, keeping `from` exactly as picked — so the window GROWS and nothing
 * that was visible yesterday disappears. It only ever extends: a `to` already on today, or in the
 * future (clock skew, a deliberately forward range), is left alone. The range stays
 * `isDefault: false`, so it is still the user's selection and the first-trade / last-import anchoring
 * still never touches it. Owner's decision, and they were told a deliberately historical window (say
 * a January review) becomes a to-date window on the next visit.
 */
const STORAGE_KEY = 'journal:dateRange';

/** Window length, in days, of the default range (last 7 days = today − 6 … today). */
const DEFAULT_WINDOW_DAYS = 7;

export interface DateRange {
  /** Inclusive start, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  to: string;
}

/** Format a Date as a local `YYYY-MM-DD` day (matches react-day-picker's local-midnight Dates). */
export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Build an inclusive `[end − (DEFAULT_WINDOW_DAYS − 1) … end]` window from a `YYYY-MM-DD` end day. */
export function windowEndingOn(endYmd: string): DateRange {
  const [y, m, d] = endYmd.split('-').map(Number) as [number, number, number];
  const end = new Date(y, m - 1, d);
  const from = new Date(end);
  from.setDate(from.getDate() - (DEFAULT_WINDOW_DAYS - 1));
  return { from: toYmd(from), to: toYmd(end) };
}

/** Default window: the last 7 days (today − 6 … today) — preserves the app's prior `7d` default. */
export function defaultDateRange(): DateRange {
  return windowEndingOn(toYmd(new Date()));
}

/**
 * Roll a restored range's END up to `today` when it sits in the PAST, leaving the START untouched.
 * Pure and total: returns the SAME object when nothing needs to change, so callers can use identity
 * to skip a write. Never pulls a future `to` back — extending is the only direction.
 */
export function extendToToday(range: DateRange, today: string): DateRange {
  return range.to < today ? { from: range.from, to: today } : range;
}

interface InitialState {
  range: DateRange;
  /** True only for the fresh device-today default (eligible for last-import re-anchoring). */
  isDefault: boolean;
}

function readInitial(): InitialState {
  if (typeof localStorage === 'undefined') return { range: defaultDateRange(), isDefault: true };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { range: defaultDateRange(), isDefault: true };
    const parsed = JSON.parse(raw) as Partial<DateRange>;
    if (parsed && typeof parsed.from === 'string' && typeof parsed.to === 'string') {
      // A persisted range is a sticky, explicit selection — the first-trade / last-import anchoring
      // never touches it. The one thing we DO move is an END left in the past: roll it up to today so
      // the window keeps covering "up to now" without the trader re-picking it every day. `from` is
      // untouched, so the window only ever grows.
      const stored = { from: parsed.from, to: parsed.to };
      const rolled = extendToToday(stored, toYmd(new Date()));
      // Persist the rolled value so storage and the store agree; skipped when nothing moved.
      if (rolled !== stored) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rolled)); } catch { /* quota / private mode */ }
      }
      return { range: rolled, isDefault: false };
    }
  } catch { /* parse error / quota / private mode */ }
  return { range: defaultDateRange(), isDefault: true };
}

interface DateRangeState {
  range: DateRange;
  /** Whether `range` is still the auto-default (not user-picked / not restored from storage). */
  isDefault: boolean;
  /** User picks a range — persists it and pins it (no further auto re-anchoring). */
  setRange: (next: DateRange) => void;
  /**
   * Re-anchor the default window's END to the account's last-import day (`YYYY-MM-DD`), keeping the
   * 7-day length. No-op once the user has picked a range (or a selection was restored from storage),
   * and a no-op if the window already ends on that day. Does NOT persist — re-anchoring is a
   * per-session convenience derived from live data, not a sticky user choice.
   */
  seedFromLastImport: (lastImportYmd: string) => void;
  /**
   * Anchor the default window's START to the selected account's FIRST-trade day (`YYYY-MM-DD`), with the
   * END = today — i.e. "from my first trade … to now". No-op once the user has picked a range (or one was
   * restored from storage), and a no-op if already anchored there. Does NOT persist — a per-session
   * default derived from live data. Re-runs while `isDefault` so switching accounts re-anchors the start.
   */
  seedFromFirstTrade: (firstTradeYmd: string) => void;
}

export const useDateRangeStore = create<DateRangeState>((set, get) => ({
  ...readInitial(),
  setRange: (next: DateRange) => {
    // Diagnostics (demo/dev/local only): confirm the store actually receives the new range.
    if (import.meta.env.MODE !== 'prod') console.log(ts(), '[date-range] setRange → store', next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota / private mode */ }
    set({ range: next, isDefault: false });
  },
  seedFromLastImport: (lastImportYmd: string) => {
    const s = get();
    if (!s.isDefault) return;                 // user-picked / persisted → leave it alone
    if (s.range.to === lastImportYmd) return; // already anchored there
    // Stay isDefault: true so a later, fresher last-import value can re-anchor again, and so a real
    // user pick still wins. Not persisted (see doc above).
    set({ range: windowEndingOn(lastImportYmd) });
  },
  seedFromFirstTrade: (firstTradeYmd: string) => {
    const s = get();
    if (!s.isDefault) return;                        // user-picked / persisted → leave it alone
    const to = toYmd(new Date());                    // today (local), matching the picker's YMD days
    const from = firstTradeYmd <= to ? firstTradeYmd : to; // never invert (defensive)
    if (s.range.from === from && s.range.to === to) return; // already anchored
    // Stay isDefault: true so switching accounts re-anchors the start, and a real user pick still wins.
    set({ range: { from, to } });
  },
}));

/** Hook returning the active global date range (inclusive `YYYY-MM-DD` from/to). */
export const useDateRange    = () => useDateRangeStore(s => s.range);
export const useSetDateRange = () => useDateRangeStore(s => s.setRange);
